// @/utils/getCountryFlags.js
/**
 * Enhanced utility for handling country flags with flexible code formats
 *
 * Provides comprehensive country code handling with intelligent format
 * detection and conversion for maximum compatibility.
 *
 * Features:
 * - ISO 3166-1 alpha-2/alpha-3 code conversion
 * - Flexible country code format handling
 * - Common country name aliases support
 * - Robust error handling with fallbacks
 * - Backward compatibility for legacy code
 *
 * MIGRATION NOTE:
 * For new code, prefer using the <CountryFlag /> component from
 * @/components/reusable/CountryFlag which renders inline SVGs
 * bundled at build time (no HTTP requests at runtime).
 *
 * Legacy getCountryFlag() function is maintained for backward compatibility
 * but will load flags via HTTP requests.
 */

// ISO 3166-1 alpha-3 to alpha-2 mapping
// This mapping is generated programmatically and covers all standard ISO codes
const ALPHA3_TO_ALPHA2 = {"AFG":"af","ALA":"ax","ALB":"al","DZA":"dz","ASM":"as","AND":"ad","AGO":"ao","AIA":"ai","ATA":"aq","ATG":"ag","ARG":"ar","ARM":"am","ABW":"aw","AUS":"au","AUT":"at","AZE":"az","BHS":"bs","BHR":"bh","BGD":"bd","BRB":"bb","BLR":"by","BEL":"be","BLZ":"bz","BEN":"bj","BMU":"bm","BTN":"bt","BOL":"bo","BES":"bq","BIH":"ba","BWA":"bw","BVT":"bv","BRA":"br","IOT":"io","BRN":"bn","BGR":"bg","BFA":"bf","BDI":"bi","CPV":"cv","KHM":"kh","CMR":"cm","CAN":"ca","CYM":"ky","CAF":"cf","TCD":"td","CHL":"cl","CHN":"cn","CXR":"cx","CCK":"cc","COL":"co","COM":"km","COG":"cg","COD":"cd","COK":"ck","CRI":"cr","CIV":"ci","HRV":"hr","CUB":"cu","CUW":"cw","CYP":"cy","CZE":"cz","DNK":"dk","DJI":"dj","DMA":"dm","DOM":"do","ECU":"ec","EGY":"eg","SLV":"sv","GNQ":"gq","ERI":"er","EST":"ee","ETH":"et","FLK":"fk","FRO":"fo","FJI":"fj","FIN":"fi","FRA":"fr","GUF":"gf","PYF":"pf","ATF":"tf","GAB":"ga","GMB":"gm","GEO":"ge","DEU":"de","GHA":"gh","GIB":"gi","GRC":"gr","GRL":"gl","GRD":"gd","GLP":"gp","GUM":"gu","GTM":"gt","GGY":"gg","GIN":"gn","GNB":"gw","GUY":"gy","HTI":"ht","HMD":"hm","VAT":"va","HND":"hn","HKG":"hk","HUN":"hu","ISL":"is","IND":"in","IDN":"id","IRN":"ir","IRQ":"iq","IRL":"ie","IMN":"im","ISR":"il","ITA":"it","JAM":"jm","JPN":"jp","JEY":"je","JOR":"jo","KAZ":"kz","KEN":"ke","KIR":"ki","PRK":"kp","KOR":"kr","KWT":"kw","KGZ":"kg","LAO":"la","LVA":"lv","LBN":"lb","LSO":"ls","LBR":"lr","LBY":"ly","LIE":"li","LTU":"lt","LUX":"lu","MAC":"mo","MKD":"mk","MDG":"mg","MWI":"mw","MYS":"my","MDV":"mv","MLI":"ml","MLT":"mt","MHL":"mh","MTQ":"mq","MRT":"mr","MUS":"mu","MYT":"yt","MEX":"mx","FSM":"fm","MDA":"md","MCO":"mc","MNG":"mn","MNE":"me","MSR":"ms","MAR":"ma","MOZ":"mz","MMR":"mm","NAM":"na","NRU":"nr","NPL":"np","NLD":"nl","NCL":"nc","NZL":"nz","NIC":"ni","NER":"ne","NGA":"ng","NIU":"nu","NFK":"nf","MNP":"mp","NOR":"no","OMN":"om","PAK":"pk","PLW":"pw","PSE":"ps","PAN":"pa","PNG":"pg","PRY":"py","PER":"pe","PHL":"ph","PCN":"pn","POL":"pl","PRT":"pt","PRI":"pr","QAT":"qa","REU":"re","ROU":"ro","RUS":"ru","RWA":"rw","BLM":"bl","SHN":"sh","KNA":"kn","LCA":"lc","MAF":"mf","SPM":"pm","VCT":"vc","WSM":"ws","SMR":"sm","STP":"st","SAU":"sa","SEN":"sn","SRB":"rs","SYC":"sc","SLE":"sl","SGP":"sg","SXM":"sx","SVK":"sk","SVN":"si","SLB":"sb","SOM":"so","ZAF":"za","SGS":"gs","SSD":"ss","ESP":"es","LKA":"lk","SDN":"sd","SUR":"sr","SJM":"sj","SWZ":"sz","SWE":"se","CHE":"ch","SYR":"sy","TWN":"tw","TJK":"tj","TZA":"tz","THA":"th","TLS":"tl","TGO":"tg","TKL":"tk","TON":"to","TTO":"tt","TUN":"tn","TUR":"tr","TKM":"tm","TCA":"tc","TUV":"tv","UGA":"ug","UKR":"ua","ARE":"ae","GBR":"gb","USA":"us","UMI":"um","URY":"uy","UZB":"uz","VUT":"vu","VEN":"ve","VNM":"vn","VGB":"vg","VIR":"vi","WLF":"wf","ESH":"eh","YEM":"ye","ZMB":"zm","ZWE":"zw"};

// For reverse lookup (alpha-2 to alpha-3)
const ALPHA2_TO_ALPHA3 = {};
Object.entries(ALPHA3_TO_ALPHA2).forEach(([alpha3, alpha2]) => {
  ALPHA2_TO_ALPHA3[alpha2.toUpperCase()] = alpha3;
});

// Common country code aliases (non-standard but found in some systems)
const COUNTRY_ALIASES = {
  // Common alternate codes
  "UK": "GB",
  "ENG": "GB",
  "SCO": "GB",
  "WLS": "GB",
  "NIR": "GB",
  "EUE": "EU", // EU is not a standard ISO code but used in the system
  
  // Common misspellings or simplifications
  "DEUTSCHLAND": "DE",
  "GERMANY": "DE",
  "FRANCE": "FR",
  "ITALY": "IT",
  "SPAIN": "ES",
  "CHINA": "CN",
  "JAPAN": "JP",
  "KOREA": "KR",
  "RUSSIA": "RU",
  "BRAZIL": "BR",
  "INDIA": "IN",
  "AMERICA": "US",
  "UNITED STATES": "US"
};

/**
 * Normalize any country code format to alpha-2
 * Handles alpha-3, alpha-2, and common aliases
 * 
 * @param {string} countryCode - Country code in any format
 * @returns {string} Normalized alpha-2 code or original if not found
 */
export const normalizeCountryCode = (countryCode) => {
  if (!countryCode) return '';
  
  // Trim and uppercase for consistent comparison
  const code = countryCode.trim().toUpperCase();
  
  // Direct lookup in mappings
  if (code.length === 2) {
    // It's already alpha-2, just lowercase it for flag filenames
    return code.toLowerCase();
  } else if (code.length === 3) {
    // It's probably alpha-3, convert to alpha-2
    return ALPHA3_TO_ALPHA2[code] || code.toLowerCase();
  } else {
    // Check aliases for longer names
    return COUNTRY_ALIASES[code] ? 
      (COUNTRY_ALIASES[code].length === 2 ? 
        COUNTRY_ALIASES[code].toLowerCase() : 
        ALPHA3_TO_ALPHA2[COUNTRY_ALIASES[code]] || '') : 
      '';
  }
};

/**
 * Converts ISO 3166-1 alpha-3 code to alpha-2 for flag filenames
 * Enhanced with better error handling and validation
 * 
 * @param {string} alpha3 - The 3-letter country code (e.g., "DEU")
 * @returns {string} The 2-letter country code (e.g., "de") or empty string if invalid
 */
export const alpha3ToAlpha2 = (alpha3) => {
  if (!alpha3) return '';
  
  // Handle non-string inputs
  if (typeof alpha3 !== 'string') {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Invalid country code format: ${alpha3}`);
    }
    return '';
  }
  
  // Normalize to uppercase for consistent lookup
  const code = alpha3.trim().toUpperCase();
  
  // If it's already in alpha-2 format, just return it
  if (code.length === 2) {
    return code.toLowerCase();
  }
  
  // Return the mapped alpha-2 code or empty string if not found
  const result = ALPHA3_TO_ALPHA2[code] || '';
  
  // Log warning for unmapped codes in development
  if (!result && process.env.NODE_ENV === 'development') {
    console.warn(`Country code not found in mapping: ${alpha3}`);
  }
  
  return result;
};

/**
 * Get flag URL for a country using any standard country code format
 * Enhanced with better fallback options and error handling
 * 
 * @param {string} countryCode - Country code in alpha-2, alpha-3, or common aliases
 * @returns {string} The URL of the flag image
 */
export const getCountryFlag = (countryCode) => {
  if (!countryCode) {
    return "/flags/blank.svg";
  }
  
  // Handle non-string inputs
  if (typeof countryCode !== 'string') {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Invalid country code format: ${countryCode}`);
    }
    return "/flags/blank.svg";
  }
  
  // Try multiple approaches to get a valid alpha-2 code
  let alpha2 = '';
  
  // First try direct normalization
  alpha2 = normalizeCountryCode(countryCode);
  
  // If that fails, try alpha3ToAlpha2 as fallback
  if (!alpha2 && countryCode.length === 3) {
    alpha2 = alpha3ToAlpha2(countryCode);
  }
  
  // Return the flag path or blank if no mapping found
  return alpha2 ? `/flags/${alpha2}.svg` : "/flags/blank.svg";
};

/**
 * Legacy compatibility function for any code that might still use country names
 * @param {string} countryName - The name of the country 
 * @returns {string} The URL of the flag image
 * @deprecated Use getCountryFlag with country code instead
 */
export const getCountryFlagByName = (countryName) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn('getCountryFlagByName is deprecated. Use getCountryFlag with country code instead.');
  }
  
  if (!countryName) return '/flags/blank.svg';
  
  // Check if the country name is actually a code
  if (countryName.length <= 3) {
    return getCountryFlag(countryName);
  }
  
  // Try alias lookup for common country names
  const uppercaseName = countryName.toUpperCase();
  if (COUNTRY_ALIASES[uppercaseName]) {
    const code = COUNTRY_ALIASES[uppercaseName];
    return getCountryFlag(code);
  }
  
  return '/flags/blank.svg';
};